/**
 * Sheet Reference Utilities
 * Handles sheet-to-sheet references in formulas exactly as Microsoft Excel does
 */

/**
 * Excel sheet name rules:
 * - Can contain letters, numbers, spaces, and some special characters
 * - Cannot contain: [ ] : * ? / \
 * - Max 31 characters
 * - Case-sensitive
 * - Must be quoted with single quotes if contains spaces or special characters
 */

/**
 * Escapes single quotes in sheet names for Excel format
 * Excel doubles single quotes: Sheet's Name becomes 'Sheet''s Name'!A1
 */
export function escapeSheetName(name) {
  if (!name) return '';
  return name.replace(/'/g, "''");
}

/**
 * Unescapes sheet names from Excel format
 * 'Sheet''s Name' becomes Sheet's Name
 */
export function unescapeSheetName(name) {
  if (!name) return '';
  return name.replace(/''/g, "'");
}

/**
 * Checks if a sheet name needs quoting in Excel
 * Names need quotes if they contain:
 * - Spaces
 * - Special characters
 * - Start with a digit
 */
export function needsQuoting(name) {
  if (!name) return false;
  
  // Check if name starts with a digit
  if (/^\d/.test(name)) return true;
  
  // Check for spaces or special characters
  if (/[\s\-+*\/()&%$#@!~`{}[\]:;"<>,.?\\|]/.test(name)) return true;
  
  return false;
}

/**
 * Quotes a sheet name for use in formulas
 * Follows Excel rules: adds single quotes and escapes internal quotes
 */
export function quoteSheetName(name) {
  if (!name) return "''";
  const escaped = escapeSheetName(name);
  return `'${escaped}'`;
}

/**
 * Formats a sheet name for use in formulas
 * Quotes only if necessary
 */
export function formatSheetName(name) {
  if (!name) return "''";
  if (needsQuoting(name)) {
    return quoteSheetName(name);
  }
  return name;
}

/**
 * Regex patterns for matching sheet references
 * Excel format: SheetName!A1 or 'Sheet Name'!A1:B2
 */

// Matches quoted sheet names: 'Sheet Name'!
const QUOTED_SHEET_PATTERN = /'([^']+(?:''[^']+)*)'!/;

// Matches unquoted sheet names: SheetName!
// Valid unquoted names contain only letters, numbers, underscore, and some chars
const UNQUOTED_SHEET_PATTERN = /([A-Za-z0-9_][A-Za-z0-9_\.]*?)!/;

// Cell reference pattern
const CELL_REF_PATTERN = /([$])?([A-Za-z]{1,3})([$])?([1-9]\d{0,5}|[1-2]\d{5}|300000)/;

// Range pattern (optional)
const RANGE_PATTERN = `:${CELL_REF_PATTERN.source}`;

/**
 * Combined pattern for full sheet reference
 * Matches: Sheet1!A1, 'Sheet 1'!A1, Sheet1!A1:B2, 'Sheet 1'!A1:B2
 */
export function createSheetReferenceRegex(global = false) {
  const quotedSheet = /'([^']+(?:''[^']+)*)'!/;
  const unquotedSheet = /([A-Za-z0-9_][A-Za-z0-9_\.]*?)!/;
  const cellRef = /([$])?([A-Za-z]{1,3})([$])?([1-9]\d{0,5})/;
  const rangeOpt = `(?::${cellRef.source})?`;
  
  // Match either quoted or unquoted sheet name, followed by cell reference
  const pattern = `(?:${quotedSheet.source}|${unquotedSheet.source})${cellRef.source}${rangeOpt}`;
  
  return new RegExp(pattern, global ? 'g' : '');
}

/**
 * Parses a sheet reference into components
 * @param {string} reference - e.g., "Sheet1!A1", "'Sheet 1'!A1:B2"
 * @returns {Object|null} - { sheetName, cellRef, isQuoted, fullMatch }
 */
export function parseSheetReference(reference) {
  if (!reference) return null;
  
  const regex = createSheetReferenceRegex(false);
  const match = reference.match(regex);
  
  if (!match) return null;
  
  // Check if it's a quoted sheet name (group 1) or unquoted (group 2)
  const isQuoted = match[1] !== undefined;
  const sheetName = isQuoted ? unescapeSheetName(match[1]) : match[2];
  
  // The cell reference starts after the sheet name and !
  const cellRefStart = match[0].indexOf('!') + 1;
  const cellRef = match[0].substring(cellRefStart);
  
  return {
    sheetName,
    cellRef,
    isQuoted,
    fullMatch: match[0],
    matchIndex: match.index
  };
}

/**
 * Finds a sheet by name (case-sensitive, exact match)
 * @param {string} sheetName - The sheet name to find
 * @param {Array} sheets - Array of sheet objects with 'name' property
 * @returns {Object|null} - The found sheet or null
 */
export function findSheetByName(sheetName, sheets) {
  if (!sheetName || !Array.isArray(sheets)) return null;
  
  // Normalize the search name (remove quotes if present, unescape)
  let searchName = sheetName.trim();
  if (searchName.startsWith("'") && searchName.endsWith("'")) {
    searchName = searchName.slice(1, -1);
    searchName = unescapeSheetName(searchName);
  }
  
  // Excel sheet names are case-sensitive
  return sheets.find(sheet => sheet && sheet.name === searchName) || null;
}

/**
 * Replaces sheet references in a formula when a sheet is renamed
 * @param {string} formula - The formula text (without leading =)
 * @param {string} oldName - Original sheet name
 * @param {string} newName - New sheet name
 * @returns {string} - Updated formula
 */
export function updateSheetReferencesOnRename(formula, oldName, newName) {
  if (!formula || !oldName || !newName) return formula;
  
  const regex = createSheetReferenceRegex(true);
  
  return formula.replace(regex, (match) => {
    const parsed = parseSheetReference(match);
    if (!parsed) return match;
    
    // Check if this reference is to the renamed sheet
    if (parsed.sheetName === oldName) {
      // Create new reference with new name
      const newSheetFormatted = formatSheetName(newName);
      return `${newSheetFormatted}!${parsed.cellRef}`;
    }
    
    return match;
  });
}

/**
 * Replaces sheet references with #REF! when a sheet is deleted
 * @param {string} formula - The formula text (without leading =)
 * @param {string} deletedName - Name of the deleted sheet
 * @returns {string} - Updated formula with #REF! errors
 */
export function updateSheetReferencesOnDelete(formula, deletedName) {
  if (!formula || !deletedName) return formula;
  
  const regex = createSheetReferenceRegex(true);
  
  return formula.replace(regex, (match) => {
    const parsed = parseSheetReference(match);
    if (!parsed) return match;
    
    // Check if this reference is to the deleted sheet
    if (parsed.sheetName === deletedName) {
      return '#REF!';
    }
    
    return match;
  });
}

/**
 * Extracts all unique sheet names referenced in a formula
 * @param {string} formula - The formula text (without leading =)
 * @returns {Array<string>} - Array of unique sheet names
 */
export function extractSheetReferences(formula) {
  if (!formula) return [];
  
  const regex = createSheetReferenceRegex(true);
  const sheetNames = new Set();
  
  let match;
  while ((match = regex.exec(formula)) !== null) {
    const parsed = parseSheetReference(match[0]);
    if (parsed) {
      sheetNames.add(parsed.sheetName);
    }
  }
  
  return Array.from(sheetNames);
}

/**
 * Validates if a sheet name is valid according to Excel rules
 * @param {string} name - Sheet name to validate
 * @returns {Object} - { valid: boolean, error: string|null }
 */
export function validateSheetName(name) {
  if (!name || name.trim() === '') {
    return { valid: false, error: 'Sheet name cannot be empty' };
  }
  
  if (name.length > 31) {
    return { valid: false, error: 'Sheet name cannot exceed 31 characters' };
  }
  
  // Check for invalid characters
  const invalidChars = /[\[\]:*?\/\\]/;
  if (invalidChars.test(name)) {
    return { valid: false, error: 'Sheet name contains invalid characters: [ ] : * ? / \\' };
  }
  
  return { valid: true, error: null };
}

export default {
  escapeSheetName,
  unescapeSheetName,
  needsQuoting,
  quoteSheetName,
  formatSheetName,
  createSheetReferenceRegex,
  parseSheetReference,
  findSheetByName,
  updateSheetReferencesOnRename,
  updateSheetReferencesOnDelete,
  extractSheetReferences,
  validateSheetName
};
